import { z } from "zod";
/**
 * Photographs of the bottle, held against the wine.
 *
 * The domain decision is in `project-scope.md` and the sizing in
 * `tech-stack.md`. What matters at the boundary is small: a photograph is a
 * row with a read URL that expires, a flag saying whether it is the one that
 * represents the wine, and nothing else the browser needs to know.
 */
/**
 * The two stored sizes, named rather than numbered so that a caller asks for
 * what it needs instead of remembering which of 1600 and 400 is which.
 *
 * `full` is what a back label is read from — importer, alcohol, sometimes a
 * disgorgement date, all of it small print. `small` is what the report embeds
 * and what a list thumbnail would use if one is ever wanted.
 */
export const photoSizeSchema = z.enum(["full", "small"]);
/**
 * The long edge of each size, in pixels. Here rather than in the browser
 * because the server writes the same numbers into the object key, and two
 * copies of 1600 in two workspaces is how they drift apart.
 */
export const PHOTO_LONG_EDGE = {
    full: 1600,
    small: 400,
};
/**
 * What the server will accept for one size, after the browser has resized.
 *
 * Generous against the ~300 kB a 1600px photograph of a bottle actually makes:
 * the resize happens on a client nobody controls, and the limit is here to
 * refuse a broken one rather than to trim a working one.
 */
export const PHOTO_MAX_BYTES = 2 * 1024 * 1024;
/**
 * The formats a browser can reliably produce from a `<canvas>` and a bucket can
 * reliably serve. **Not HEIC**, which is what an iPhone photographs in: the
 * upload sends what the canvas encoded, and the canvas encodes JPEG.
 *
 * The decode side is the one that bites, and it is handled in the browser —
 * see `resizeForUpload`. A file that cannot be decoded is refused there, with a
 * message, rather than arriving here as a white rectangle that looks like a
 * successful upload.
 */
export const photoContentTypeSchema = z.enum(["image/jpeg"]);
export const winePhotoSchema = z.object({
    id: z.string(),
    /**
     * A URL that works for a while and then does not. Reads are signed so that a
     * photograph is no more public than the cellar it belongs to; the expiry is
     * why the browser must not cache these across sessions or store them.
     */
    url: z.string(),
    /** The same photograph at 400px, for anywhere a thumbnail is wanted. */
    smallUrl: z.string(),
    /**
     * Whether this is the one that represents the wine: shown first on its page,
     * and the one the report embeds. Exactly one photograph of a wine carries
     * it, which the write path holds rather than the database.
     */
    isDesignated: z.boolean(),
    uploadedAt: z.string(),
});
/**
 * Added to the wine's own response at `9.7`, defaulted for that one release.
 *
 * The frontend deploys on a green `main` and the backend never does, so the
 * browser could arrive before the API that sends this, and `.default([])` was
 * what let it: a page that asked an older API for a wine got a wine with no
 * photographs rather than a parse failure at the boundary. That API has been
 * serving since, so this is the later commit the note promised — `10.7`. A
 * wine that now arrives without the field is drift, and drift is supposed to
 * be loud.
 */
export const winePhotosField = z.array(winePhotoSchema);
/**
 * One size arriving from the browser. Both are uploaded in one request,
 * because a wine with a full-size photograph and no thumbnail is a state
 * nothing wants to handle and a second request is a second chance to fail.
 */
const uploadedImage = z.object({
    /** Base64, no data-URL prefix. */
    data: z.string().min(1),
    contentType: photoContentTypeSchema,
    width: z.number().int().positive(),
    height: z.number().int().positive(),
});
export const uploadWinePhotoSchema = z.object({
    full: uploadedImage,
    small: uploadedImage,
    /**
     * Whether this one should represent the wine. The first photograph of a wine
     * is designated whatever this says — a wine with photographs and none of them
     * designated has nothing to show on its page or put in the report, and that
     * is a state the write path refuses to create rather than a choice to offer.
     */
    designate: z.boolean().default(false),
});
//# sourceMappingURL=photo.js.map